import { checkUrl, getProxyParams, type ImgPreset } from "@/api/shared/imgproxy";
import { useSnackStore } from "@/stores/snackStore";
import { EFileType, getFileType } from "@/composables/shared/useFile";
import type { TFile } from "@/api/shared/file";

export type TImgInput = string | TFile | null | undefined;
export type ImgSize = "xs" | "sm" | "md" | "lg" | "xl" | "xxl" | "original";

const IMG_PRESETS = Object.freeze({
  xs: { w: 128, h: 128, q: 60 },
  sm: { w: 256, h: 256, q: 70 },
  md: { w: 512, h: 512, q: 75 },
  lg: { w: 1024, h: 1024, q: 80 },
  xl: { w: 2048, h: 2048, q: 85 },
  xxl: { w: 4096, h: 4096, q: 90 },
  original: { q: 82 },
} satisfies Record<ImgSize, ImgPreset>);

export const LOCAL_FILE_PREFIX = "/src/";
const BASE_URL = import.meta.env.BASE_URL ?? "/";

const isLikelyLocalUrl = (url: string) =>
  url.startsWith("blob:") ||
  url.startsWith("data:") ||
  url.startsWith(LOCAL_FILE_PREFIX) ||
  url.startsWith("/assets/") ||
  url.startsWith("/temp/") ||
  url.startsWith(`${BASE_URL}assets/`) ||
  url.startsWith(`${BASE_URL}src/`) ||
  checkUrl(url);

function resolveImg(input: TImgInput): {
  url: string | null;
  isImage: boolean;
  canProxy: boolean;
} {
  if (!input) return { url: null, isImage: false, canProxy: false };

  if (typeof input === "string") {
    return { url: input, isImage: true, canProxy: !isLikelyLocalUrl(input) };
  }

  return {
    url: input.file,
    isImage: getFileType(input) === EFileType.IMAGE,
    canProxy: !isLikelyLocalUrl(input.file),
  };
}

export const useImgProxy = () => {
  const { showError } = useSnackStore();

  const getImgProxyUrl = (image: TImgInput, size: ImgSize = "md") => {
    if (image === undefined || image === null || (typeof image === "object" && image.file === "")) {
      return;
    }

    const { url, isImage, canProxy } = resolveImg(image);

    if (!url) {
      showError("Given file url doesn't exist");
      return;
    }

    if (!isImage) {
      showError("File is not an image");
      return;
    }

    return canProxy ? url + getProxyParams(IMG_PRESETS[size]) : url;
  };

  return { getImgProxyUrl };
};
